/**
 * Comprehensive database of confusable/homoglyph Unicode characters
 * These characters look similar but are from different Unicode scripts
 * Used for detecting phishing, spoofing, and filter evasion attempts
 */

export interface ConfusableChar {
  char: string;
  code: number;
  script: string;
  desc: string;
}

/**
 * Map of base characters to their confusable lookalikes
 * Key: Latin character, Value: Array of visually similar characters from other scripts
 */
export const CONFUSABLES: Record<string, ConfusableChar[]> = {
  // Uppercase Latin letters
  'A': [
    { char: 'А', code: 0x0410, script: 'Cyrillic', desc: 'Cyrillic Capital Letter A' },
    { char: 'Α', code: 0x0391, script: 'Greek', desc: 'Greek Capital Letter Alpha' },
    { char: 'Ꭺ', code: 0x13AA, script: 'Cherokee', desc: 'Cherokee Letter Go' },
  ],
  'B': [
    { char: 'В', code: 0x0412, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Ve' },
    { char: 'Β', code: 0x0392, script: 'Greek', desc: 'Greek Capital Letter Beta' },
    { char: 'Ᏼ', code: 0x13FC, script: 'Cherokee', desc: 'Cherokee Letter Yv' },
  ],
  'C': [
    { char: 'С', code: 0x0421, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Es' },
    { char: 'Ϲ', code: 0x03F9, script: 'Greek', desc: 'Greek Capital Lunate Sigma' },
    { char: 'Ⲥ', code: 0x2CA4, script: 'Coptic', desc: 'Coptic Capital Letter Sima' },
  ],
  'D': [
    { char: 'Ꭰ', code: 0x13A0, script: 'Cherokee', desc: 'Cherokee Letter A' },
  ],
  'E': [
    { char: 'Е', code: 0x0415, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Ie' },
    { char: 'Ε', code: 0x0395, script: 'Greek', desc: 'Greek Capital Letter Epsilon' },
  ],
  'F': [
    { char: 'Ϝ', code: 0x03DC, script: 'Greek', desc: 'Greek Letter Digamma' },
  ],
  'G': [
    { char: 'Ԍ', code: 0x050C, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Komi De' },
  ],
  'H': [
    { char: 'Н', code: 0x041D, script: 'Cyrillic', desc: 'Cyrillic Capital Letter En' },
    { char: 'Η', code: 0x0397, script: 'Greek', desc: 'Greek Capital Letter Eta' },
  ],
  'I': [
    { char: 'І', code: 0x0406, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Byelorussian-Ukrainian I' },
    { char: 'Ι', code: 0x0399, script: 'Greek', desc: 'Greek Capital Letter Iota' },
    { char: 'Ⅰ', code: 0x2160, script: 'Roman', desc: 'Roman Numeral One' },
  ],
  'J': [
    { char: 'Ј', code: 0x0408, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Je' },
  ],
  'K': [
    { char: 'К', code: 0x041A, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Ka' },
    { char: 'Κ', code: 0x039A, script: 'Greek', desc: 'Greek Capital Letter Kappa' },
  ],
  'L': [
    { char: 'Ⅼ', code: 0x216C, script: 'Roman', desc: 'Roman Numeral Fifty' },
  ],
  'M': [
    { char: 'М', code: 0x041C, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Em' },
    { char: 'Μ', code: 0x039C, script: 'Greek', desc: 'Greek Capital Letter Mu' },
  ],
  'N': [
    { char: 'Ν', code: 0x039D, script: 'Greek', desc: 'Greek Capital Letter Nu' },
  ],
  'O': [
    { char: 'О', code: 0x041E, script: 'Cyrillic', desc: 'Cyrillic Capital Letter O' },
    { char: 'Ο', code: 0x039F, script: 'Greek', desc: 'Greek Capital Letter Omicron' },
    { char: '০', code: 0x09E6, script: 'Bengali', desc: 'Bengali Digit Zero' },
    { char: '੦', code: 0x0A66, script: 'Gurmukhi', desc: 'Gurmukhi Digit Zero' },
  ],
  'P': [
    { char: 'Р', code: 0x0420, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Er' },
    { char: 'Ρ', code: 0x03A1, script: 'Greek', desc: 'Greek Capital Letter Rho' },
  ],
  'S': [
    { char: 'Ѕ', code: 0x0405, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Dze' },
  ],
  'T': [
    { char: 'Т', code: 0x0422, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Te' },
    { char: 'Τ', code: 0x03A4, script: 'Greek', desc: 'Greek Capital Letter Tau' },
  ],
  'V': [
    { char: 'Ѵ', code: 0x0474, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Izhitsa' },
  ],
  'X': [
    { char: 'Х', code: 0x0425, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Ha' },
    { char: 'Χ', code: 0x03A7, script: 'Greek', desc: 'Greek Capital Letter Chi' },
  ],
  'Y': [
    { char: 'У', code: 0x0423, script: 'Cyrillic', desc: 'Cyrillic Capital Letter U' },
    { char: 'Υ', code: 0x03A5, script: 'Greek', desc: 'Greek Capital Letter Upsilon' },
  ],
  'Z': [
    { char: 'Ζ', code: 0x0396, script: 'Greek', desc: 'Greek Capital Letter Zeta' },
  ],
  
  // Lowercase Latin letters
  'a': [
    { char: 'а', code: 0x0430, script: 'Cyrillic', desc: 'Cyrillic Small Letter A' },
    { char: 'α', code: 0x03B1, script: 'Greek', desc: 'Greek Small Letter Alpha' },
  ],
  'c': [
    { char: 'с', code: 0x0441, script: 'Cyrillic', desc: 'Cyrillic Small Letter Es' },
    { char: 'ϲ', code: 0x03F2, script: 'Greek', desc: 'Greek Lunate Sigma Symbol' },
  ],
  'e': [
    { char: 'е', code: 0x0435, script: 'Cyrillic', desc: 'Cyrillic Small Letter Ie' },
    { char: 'ε', code: 0x03B5, script: 'Greek', desc: 'Greek Small Letter Epsilon' },
  ],
  'i': [
    { char: 'і', code: 0x0456, script: 'Cyrillic', desc: 'Cyrillic Small Letter Byelorussian-Ukrainian I' },
    { char: 'ι', code: 0x03B9, script: 'Greek', desc: 'Greek Small Letter Iota' },
  ],
  'j': [
    { char: 'ј', code: 0x0458, script: 'Cyrillic', desc: 'Cyrillic Small Letter Je' },
  ],
  'o': [
    { char: 'о', code: 0x043E, script: 'Cyrillic', desc: 'Cyrillic Small Letter O' },
    { char: 'ο', code: 0x03BF, script: 'Greek', desc: 'Greek Small Letter Omicron' },
  ],
  'p': [
    { char: 'р', code: 0x0440, script: 'Cyrillic', desc: 'Cyrillic Small Letter Er' },
    { char: 'ρ', code: 0x03C1, script: 'Greek', desc: 'Greek Small Letter Rho' },
  ],
  's': [
    { char: 'ѕ', code: 0x0455, script: 'Cyrillic', desc: 'Cyrillic Small Letter Dze' },
  ],
  'x': [
    { char: 'х', code: 0x0445, script: 'Cyrillic', desc: 'Cyrillic Small Letter Ha' },
    { char: 'χ', code: 0x03C7, script: 'Greek', desc: 'Greek Small Letter Chi' },
  ],
  'y': [
    { char: 'у', code: 0x0443, script: 'Cyrillic', desc: 'Cyrillic Small Letter U' },
    { char: 'ү', code: 0x04AF, script: 'Cyrillic', desc: 'Cyrillic Small Letter Straight U' },
    { char: 'γ', code: 0x03B3, script: 'Greek', desc: 'Greek Small Letter Gamma' },
  ],
  
  // Numbers
  '0': [
    { char: 'О', code: 0x041E, script: 'Cyrillic', desc: 'Cyrillic Capital Letter O' },
    { char: 'Ο', code: 0x039F, script: 'Greek', desc: 'Greek Capital Letter Omicron' },
    { char: 'о', code: 0x043E, script: 'Cyrillic', desc: 'Cyrillic Small Letter O' },
  ],
  '1': [
    { char: 'l', code: 0x006C, script: 'Latin', desc: 'Latin Small Letter L' },
    { char: 'I', code: 0x0049, script: 'Latin', desc: 'Latin Capital Letter I' },
    { char: 'І', code: 0x0406, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Byelorussian-Ukrainian I' },
  ],
  '3': [
    { char: 'З', code: 0x0417, script: 'Cyrillic', desc: 'Cyrillic Capital Letter Ze' },
  ],
  '5': [
    { char: 'Ƽ', code: 0x01BC, script: 'Latin', desc: 'Latin Capital Letter Tone Five' },
  ],
  '6': [
    { char: 'б', code: 0x0431, script: 'Cyrillic', desc: 'Cyrillic Small Letter Be' },
  ],
};

/**
 * Reverse lookup: map any confusable character to its Latin equivalent
 */
export const CONFUSABLE_TO_LATIN: Map<string, string> = new Map();

// Build reverse lookup
Object.entries(CONFUSABLES).forEach(([latin, confusables]) => {
  confusables.forEach(conf => {
    CONFUSABLE_TO_LATIN.set(conf.char, latin);
  });
});

/**
 * Check if a character is a known confusable character
 */
export function isConfusable(char: string): boolean {
  return CONFUSABLE_TO_LATIN.has(char);
}

/**
 * Get the Latin equivalent of a confusable character
 */
export function getLatinEquivalent(char: string): string | undefined {
  return CONFUSABLE_TO_LATIN.get(char);
}

/**
 * Get all confusables for a given Latin character
 */
export function getConfusablesFor(latinChar: string): ConfusableChar[] {
  return CONFUSABLES[latinChar] || [];
}

